/* eslint-disable @typescript-eslint/no-unsafe-assignment */
/* eslint-disable @typescript-eslint/no-unsafe-member-access */
import type { Table } from '@tanstack/react-table';
import type { File } from '../../../../types/file.types';
import { FileListHeader } from './FileListHeader';
import { flexRender } from '@tanstack/react-table';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import { useNavigate } from 'react-router-dom';
import { useFileActions } from '@/hooks/useFileActions';
import { useTour } from '@reactour/tour';

interface FileListTableProps {
  table: Table<File>;
  error: Error | null;
  onDragEnd?: (
    activeId: string,
    overId: string | null,
    items: string[]
  ) => Promise<void>;
  isDragEnabled?: boolean;
}

export const FileListTable = ({
  table,
  error,
  onDragEnd,
  isDragEnabled = false,
}: FileListTableProps) => {
  const navigate = useNavigate();
  const { toggleRead } = useFileActions();
  const { isOpen } = useTour();

  const handleRowClick = (row: File) => {
    if (isOpen) return;
    sessionStorage.setItem('fileId', row.id);
    toggleRead(row.id, false);
    navigate('/files/' + row.id, { state: { file: row } });
  };

  const rows = table.getRowModel().rows;

  if (!isDragEnabled) {
    return (
      <div className="mt-2">
        <div className="file-list-table table-container relative max-h-[calc(100vh-330px)] overflow-y-auto rounded-md border border-[#e5e7eb] dark:border-[#374151] md:max-h-[calc(100vh-270px)]">
          <table className="w-full min-w-full table-fixed divide-y divide-gray-200 overflow-y-auto dark:divide-gray-700">
            <FileListHeader table={table} />
            {error ? (
              <tbody className="p-4 text-center text-red-500">
                <tr>
                  <td
                    colSpan={table.getAllColumns().length}
                    className="bg-neutralBG p-6 dark:bg-[#2D2D2D]"
                  >
                    Une erreur est survenue lors du chargement des fichiers,
                    veuillez rafraîchir la page.
                  </td>
                </tr>
              </tbody>
            ) : (
              <tbody>
                {rows?.length ? (
                  rows.map((row) => (
                    <tr
                      key={row.original.id}
                      onClick={() => handleRowClick(row.original)}
                      className={`file-list-row group relative cursor-pointer transition-colors ${
                        row.original.lu
                          ? 'bg-[#f2f3f8] hover:bg-[#e8eaf3] dark:bg-[#161617] dark:hover:bg-[#1d1d1f]'
                          : 'bg-neutralBG hover:bg-[#f5f5f7] dark:bg-[#2D2D2D] dark:hover:bg-[#353535]'
                      }`}
                    >
                      {row.getVisibleCells().map((cell) => (
                        <td
                          key={cell.id}
                          className="border-t border-neutralText border-opacity-15 text-neutralText dark:border-neutralText-dark dark:border-opacity-20 dark:text-neutralText-dark"
                        >
                          {flexRender(
                            cell.column.columnDef.cell,
                            cell.getContext()
                          )}
                        </td>
                      ))}
                    </tr>
                  ))
                ) : (
                  <tr></tr>
                )}
              </tbody>
            )}
          </table>
        </div>
      </div>
    );
  }

  // Drag & drop activé
  return (
    <div className="mt-2">
      <div className="file-list-table table-container relative max-h-[calc(100vh-330px)] overflow-y-auto rounded-md border border-[#e5e7eb] dark:border-[#374151] md:max-h-[calc(100vh-270px)]">
        <DragDropContext
          onDragEnd={(result) => {
            const { source, destination } = result as {
              source: { index: number };
              destination: { index: number } | null;
            };
            if (
              destination &&
              source.index !== destination.index &&
              onDragEnd
            ) {
              const items = rows.map((row) => row.original.id);
              onDragEnd(items[source.index], items[destination.index], items);
            }
          }}
        >
          <table className="w-full min-w-full table-fixed divide-y divide-gray-200 overflow-y-auto dark:divide-gray-700">
            <FileListHeader table={table} />
            <Droppable droppableId="files">
              {(provided) => (
                <tbody ref={provided.innerRef} {...provided.droppableProps}>
                  {rows?.length ? (
                    rows.map((row, index) => (
                      <Draggable
                        key={row.original.id}
                        draggableId={row.original.id}
                        index={index}
                      >
                        {(provided, snapshot) => (
                          <tr
                            ref={provided.innerRef}
                            {...provided.draggableProps}
                            {...provided.dragHandleProps}
                            onClick={() => handleRowClick(row.original)}
                            className={`file-list-row group relative cursor-pointer transition-colors ${
                              row.original.lu
                                ? 'bg-[#f2f3f8] hover:bg-[#e8eaf3] dark:bg-[#161617] dark:hover:bg-[#1d1d1f]'
                                : 'bg-neutralBG hover:bg-[#f5f5f7] dark:bg-[#2D2D2D] dark:hover:bg-[#353535]'
                            } ${snapshot.isDragging ? 'z-10 flex items-center justify-start gap-9 opacity-70' : ''}`}
                          >
                            {row.getVisibleCells().map((cell) => (
                              <td
                                key={cell.id}
                                className="border-t border-neutralText border-opacity-15 text-neutralText dark:border-neutralText-dark dark:border-opacity-20 dark:text-neutralText-dark"
                              >
                                {flexRender(
                                  cell.column.columnDef.cell,
                                  cell.getContext()
                                )}
                              </td>
                            ))}
                          </tr>
                        )}
                      </Draggable>
                    ))
                  ) : (
                    <tr></tr>
                  )}
                  {provided.placeholder}
                </tbody>
              )}
            </Droppable>
          </table>
        </DragDropContext>
      </div>
    </div>
  );
};
